/**
 * Page: RestaurantSettings
 * Author: Dine AI
 * Last Modified: December 17, 2025 - Light mode conversion
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  User,
  MapPin,
  CreditCard,
  Bell,
  LogOut,
  Building2,
  Users,
  History,
  ExternalLink,
  ImagePlus,
  Trash2,
  Loader2
} from 'lucide-react';
import { restaurantApi } from '@/services/api/restaurantApi';
import { apiClient } from '@/services/api/apiClient';
import { stripeApi } from '@/services/api/stripeApi';
import { useToast } from '@/components/ui/use-toast';
import LocationEditModal from '@/components/LocationEditModal';
import SubscriptionManagement from '@/components/SubscriptionManagement';
import TeamManagement from '@/components/TeamManagement';
import Cookies from 'js-cookie';

const RestaurantSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [activeSection, setActiveSection] = useState('profile');
  const [restaurantData, setRestaurantData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState<any[]>([]);
  const [hasMultipleLocations, setHasMultipleLocations] = useState(false);
  const [updatingLocation, setUpdatingLocation] = useState<number | null>(null);
  const [editingLocation, setEditingLocation] = useState<any | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loginHistory, setLoginHistory] = useState<any[]>([]);
  const [notificationPrefs, setNotificationPrefs] = useState({
    weekly_reports: true,
    marketing_tips: true
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [virtualAssistantAvailable, setVirtualAssistantAvailable] = useState(false);

  // Logo upload state
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  // User info will come from restaurant data
  const userInfo = restaurantData ? {
    name: restaurantData.owner_full_name || '',
    email: restaurantData.email || '',
    first_name: restaurantData.owner_first_name || '',
    last_name: restaurantData.owner_last_name || ''
  } : null;

  // Handle URL hash for section navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['profile', 'subscription', 'locations', 'team', 'notifications', 'purchases'].includes(hash)) {
      setActiveSection(hash);
    } else if (!hash) {
      // Default to profile section and update URL
      setActiveSection('profile');
      window.location.hash = 'profile';
    }
  }, [location.hash]);

  // Fetch restaurant profile and locations
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch profile, locations, login history, notification preferences, and navigation state in parallel
        const [profile, locationsResponse, historyResponse, notifResponse, navResponse] = await Promise.all([
          restaurantApi.getProfile(),
          apiClient.get('/locations'),
          apiClient.get('/settings/login-history').catch((err) => {
            console.log('Login history fetch failed:', err);
            return { data: [] };
          }),
          apiClient.get('/settings/notifications').catch((err) => {
            console.log('Notifications fetch failed:', err);
            return {
              data: {
                notification_weekly_reports: true,
                notification_marketing_tips: true
              }
            };
          }),
          apiClient.get('/dashboard/navigation').catch((err) => {
            console.log('Navigation state fetch failed:', err);
            return { data: { virtual_assistant_available: false, virtual_host_available: false } };
          })
        ]);
        
        setRestaurantData(profile);
        
        // Process locations
        const locationsList = Array.isArray(locationsResponse.data) ? locationsResponse.data : [];
        setLocations(locationsList);
        setHasMultipleLocations(locationsList.length > 1);
        
        // Set login history
        setLoginHistory(Array.isArray(historyResponse.data) ? historyResponse.data.slice(0, 5) : []);
        
        // Set notification preferences
        setNotificationPrefs({
          weekly_reports: notifResponse.data?.notification_weekly_reports ?? true,
          marketing_tips: notifResponse.data?.notification_marketing_tips ?? true
        });

        // Set subscription availability for sidebar filtering
        setVirtualAssistantAvailable(navResponse.data?.virtual_assistant_available ?? false);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Fetch logo when restaurant data loads
  useEffect(() => {
    if (restaurantData?.restaurant_logo_url) {
      setLogoUrl(restaurantData.restaurant_logo_url);
    }
  }, [restaurantData]);

  /**
   * Handle logo file upload
   */
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a PNG, JPEG, or WebP image',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be less than 2MB',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploadingLogo(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/virtual-host/settings/branding/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setLogoUrl(response.data.logo_url);
        toast({
          title: 'Logo uploaded',
          description: 'Your restaurant logo has been updated'
        });
      } else {
        toast({
          title: 'Upload failed',
          description: response.data.message || 'Failed to upload logo',
          variant: 'destructive'
        });
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload logo',
        variant: 'destructive'
      });
    } finally {
      setUploadingLogo(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Handle logo deletion
   */
  const handleLogoDelete = async () => {
    if (!logoUrl) return;

    try {
      setDeletingLogo(true);

      const response = await apiClient.delete('/virtual-host/settings/branding/remove-logo');

      if (response.data.success) {
        setLogoUrl(null);
        toast({
          title: 'Logo removed',
          description: 'Your restaurant logo has been removed'
        });
      } else {
        toast({
          title: 'Remove failed',
          description: response.data.message || 'Failed to remove logo',
          variant: 'destructive'
        });
      }
    } catch (error: any) {
      console.error('Error deleting logo:', error);
      toast({
        title: 'Remove failed',
        description: error.response?.data?.detail || 'Failed to remove logo',
        variant: 'destructive'
      });
    } finally {
      setDeletingLogo(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint to invalidate session
      const token = Cookies.get('auth_token');
      if (token) {
        try {
          await fetch(`${import.meta.env.VITE_API_URL || 'https://dineai.ai/api'}/v1/auth/logout`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('[Logout] Backend session invalidated');
        } catch (backendError) {
          console.warn('[Logout] Backend logout failed:', backendError);
        }
      }

      // Clear all auth-related cookies
      Cookies.remove('auth_token');
      Cookies.remove('demo_token');
      Cookies.remove('refresh_token');

      // Clear all localStorage items
      localStorage.removeItem('isDemo');
      localStorage.removeItem('demoPlan');
      localStorage.removeItem('demoAuthToken');
      localStorage.removeItem('current_rest_id');
      localStorage.removeItem('auth_provider');
      localStorage.removeItem('selected_plan');
      localStorage.removeItem('extraction_session_token');
      localStorage.removeItem('pending_extraction');
      localStorage.removeItem('extraction_items_count');
      localStorage.removeItem('extraction_was_cached');
      localStorage.removeItem('oauth_signup_flow');
      localStorage.removeItem('activation_terms_accepted');
      localStorage.removeItem('activation_privacy_accepted');
      localStorage.removeItem('activation_email_opt_in');
      localStorage.removeItem('activation_session_token');

      // Clear session storage
      sessionStorage.clear();

      console.log('[Logout] All local data cleared');
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });

      // Hard redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/';
    }
  };

  const toggleLocationStatus = async (location: any) => {
    if (location.is_current && location.location_status === 'active') {
      return; // Already handled by disabled state
    }
    
    setUpdatingLocation(location.rest_id);
    try {
      const newStatus = location.location_status === 'active' ? 'inactive' : 'active';
      await apiClient.put(`/locations/${location.rest_id}/status`, {
        status: newStatus
      });
      
      // Update local state
      setLocations(prev => prev.map(loc => 
        loc.rest_id === location.rest_id 
          ? { ...loc, location_status: newStatus }
          : loc
      ));
    } catch (error) {
      console.error('Failed to update location status:', error);
      alert('Failed to update location status. Please try again.');
    } finally {
      setUpdatingLocation(null);
    }
  };

  const handleSwitchLocation = async (location: any) => {
    if (location.is_current) {
      return; // Already current
    }
    
    setUpdatingLocation(location.rest_id);
    try {
      await apiClient.post('/locations/switch', {
        rest_id: location.rest_id
      });
      
      // Refresh the page to update all data with new location
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch location:', error);
      alert('Failed to switch location. Please try again.');
    } finally {
      setUpdatingLocation(null);
    }
  };

  const handleEditLocation = (location: any) => {
    setEditingLocation(location);
    setShowEditModal(true);
  };

  const handleEditSuccess = async () => {
    // Refresh the data after successful edit
    try {
      const [profile, locationsResponse] = await Promise.all([
        restaurantApi.getProfile(),
        apiClient.get('/locations')
      ]);
      
      setRestaurantData(profile);
      const locationsList = Array.isArray(locationsResponse.data) ? locationsResponse.data : [];
      setLocations(locationsList);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };

  const handleAddLocation = () => {
    // Navigate to dedicated location setup page
    navigate('/location-setup');
  };

  // Determine if user has multi-location plan and calculate limits
  const isMultiLocationPlan = restaurantData?.subscription_plan === 'delicious' || restaurantData?.subscription_plan === 'gourmet';
  
  // Calculate location limits based on plan
  const getLocationLimit = () => {
    switch (restaurantData?.subscription_plan) {
      case 'yummy': return 1;
      case 'delicious': return 2;
      case 'gourmet': return 4;
      default: return 1;
    }
  };
  
  const locationLimit = getLocationLimit();
  const canAddLocation = isMultiLocationPlan && locations.length < locationLimit;
  
  // Build settings sections based on plan and subscription types
  const settingsSections = [
    { id: 'profile', label: 'Profile', icon: User },
    // Only show Location Management for multi-location plans AND if they have multiple locations
    ...(isMultiLocationPlan && hasMultipleLocations ? [{ id: 'locations', label: 'Location Management', icon: MapPin }] : []),
    // Only show Team Management for Virtual Assistant (Waiter Staff) subscribers
    ...(virtualAssistantAvailable ? [{ id: 'team', label: 'Team Management', icon: Users }] : []),
    { id: 'subscription', label: 'Subscription Plan', icon: CreditCard },
    { id: 'purchases', label: 'Purchase History', icon: History },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600 text-xl">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/restaurant-dashboard')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
                <span className="text-gray-600 text-sm font-medium">Settings</span>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all text-gray-700"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </button>
            </div>

            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Restaurant Settings</h1>
              <p className="text-lg text-gray-600">
                Manage your restaurant profile, locations, and preferences
              </p>
            </div>
          </div>
        </motion.div>

        {/* Settings Content */}
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <nav className="space-y-1">
                {settingsSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      setActiveSection(section.id);
                      // Update URL hash to match section
                      window.location.hash = section.id;
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      activeSection === section.id
                        ? 'bg-[#FDB913] text-black font-semibold'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <section.icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{section.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Settings Content Area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-3"
          >
            {/* Profile Section */}
            {activeSection === 'profile' && (
              <div className="space-y-4">
                {/* Restaurant Logo Upload */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Restaurant Logo</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Upload your restaurant logo. It will appear in the dashboard header and customer-facing pages.
                  </p>

                  {/* Logo Preview Area */}
                  <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 mb-4">
                    {logoUrl ? (
                      <div className="relative group">
                        <img
                          src={logoUrl}
                          alt="Restaurant Logo"
                          className="max-w-[200px] max-h-[120px] object-contain rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <button
                            onClick={handleLogoDelete}
                            disabled={deletingLogo}
                            className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors disabled:opacity-50"
                          >
                            {deletingLogo ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <Trash2 className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-3 bg-gray-200 rounded-full flex items-center justify-center">
                          <ImagePlus className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">No logo uploaded</p>
                        <p className="text-gray-400 text-xs mt-1">
                          Dine AI logo will be shown in the header
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Upload Button */}
                  <div className="flex items-center justify-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload-va"
                    />
                    <label
                      htmlFor="logo-upload-va"
                      className={`flex items-center gap-2 px-4 py-2 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg font-medium cursor-pointer transition-colors ${
                        uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-4 h-4" />
                          <span>{logoUrl ? 'Replace Logo' : 'Upload Logo'}</span>
                        </>
                      )}
                    </label>

                    {logoUrl && (
                      <button
                        onClick={handleLogoDelete}
                        disabled={deletingLogo}
                        className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors disabled:opacity-50"
                      >
                        {deletingLogo ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Removing...</span>
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            <span>Remove</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Guidelines */}
                  <p className="text-xs text-gray-400 text-center mt-3">
                    PNG, JPEG, or WebP • Max 2MB
                  </p>
                </div>

                {!hasMultipleLocations ? (
                  // Single location display
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{restaurantData?.restaurant_name}</h3>
                        <p className="text-gray-500 text-sm">{restaurantData?.cuisine_type}</p>
                      </div>
                      <button
                        onClick={() => handleEditLocation({
                          rest_id: restaurantData?.rest_id,
                          rest_nm: restaurantData?.restaurant_name,
                          address: restaurantData?.address,
                          city: restaurantData?.city,
                          state: restaurantData?.state,
                          zip: restaurantData?.zip_code,
                          phone: restaurantData?.phone_number,
                          location_id: 1,
                          is_primary: true,
                          is_current: true
                        })}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-500 text-sm mb-1">Address</p>
                          <p className="text-gray-900">{restaurantData?.address}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-sm mb-1">City, State</p>
                          <p className="text-gray-900">{restaurantData?.city}, {restaurantData?.state} {restaurantData?.zip_code}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-sm mb-1">Phone</p>
                          <p className="text-gray-900">{restaurantData?.phone_number}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-sm mb-1">Email</p>
                          <p className="text-gray-900">{restaurantData?.email || userInfo.email}</p>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-gray-500 text-sm mb-1">Owner</p>
                        <p className="text-gray-900">
                          {restaurantData?.owner_full_name ||
                           `${restaurantData?.owner_first_name || ''} ${restaurantData?.owner_last_name || ''}`.trim() ||
                           userInfo.name}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Multiple locations display
                  <>
                    {locations.map((location) => (
                      <div key={location.rest_id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <Building2 className="h-6 w-6 text-[#FDB913]" />
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                Location #{location.location_id}
                                {location.is_primary && (
                                  <span className="text-xs bg-[#FDB913]/20 text-[#b8860b] px-2 py-0.5 rounded">Primary</span>
                                )}
                                {location.is_current && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Current</span>
                                )}
                              </h3>
                              <p className="text-gray-500 text-sm">{location.rest_nm}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleEditLocation(location)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-gray-500 text-sm mb-1">Address</p>
                            <p className="text-gray-900">{location.address}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm mb-1">City, State</p>
                            <p className="text-gray-900">{location.city}, {location.state} {location.zip}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm mb-1">Phone</p>
                            <p className="text-gray-900">{location.phone}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm mb-1">Status</p>
                            <p className={`font-medium ${location.location_status === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                              {location.location_status === 'active' ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Login History for multiple locations */}
                    {loginHistory.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                        <h3 className="text-gray-900 font-medium mb-3">Recent Login Activity</h3>
                        <div className="space-y-2">
                          {loginHistory.map((login, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                              <div className="text-gray-600">
                                <span className="text-gray-900">{login.browser || 'Unknown'}</span> on {login.os || 'Unknown'}
                              </div>
                              <div className="text-gray-400">
                                {new Date(login.login_at).toLocaleDateString()} at {new Date(login.login_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Location Management Section - Only for multi-location */}
            {activeSection === 'locations' && isMultiLocationPlan && hasMultipleLocations && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Location Management</h2>
                <p className="text-gray-600 mb-6">
                  Enable or disable locations. Disabled locations won't appear to customers.
                </p>

                <div className="space-y-3">
                  {locations.map((location) => (
                    <div key={location.rest_id} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Building2 className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-gray-900 font-medium">
                            {location.city} - Location #{location.location_id}
                          </p>
                          <p className="text-gray-500 text-sm">{location.address}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {location.is_current && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Current</span>
                        )}
                        {!location.is_current && location.location_status === 'active' && (
                          <button
                            onClick={() => handleSwitchLocation(location)}
                            disabled={updatingLocation === location.rest_id}
                            className="px-3 py-1 bg-[#FDB913] text-black rounded-lg text-sm font-medium hover:bg-[#e5a711] transition-colors disabled:opacity-50"
                          >
                            {updatingLocation === location.rest_id ? 'Switching...' : 'Switch to this location'}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (location.is_current && location.location_status === 'active') {
                              alert("Cannot deactivate your current location. Please switch to another location first.");
                              return;
                            }
                            toggleLocationStatus(location);
                          }}
                          disabled={
                            (location.is_current && location.location_status === 'active') ||
                            updatingLocation === location.rest_id
                          }
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            location.location_status === 'active'
                              ? 'bg-[#FDB913]'
                              : 'bg-gray-300'
                          } ${
                            (location.is_current && location.location_status === 'active') ||
                            updatingLocation === location.rest_id
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            location.location_status === 'active' ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-[#FDB913]/10 rounded-lg border border-[#FDB913]/30">
                  <p className="text-[#b8860b] text-sm">
                    <strong>Note:</strong> You cannot deactivate your current location. To deactivate it, switch to another location first from the dashboard.
                  </p>
                </div>

                {/* Add Location button with plan limit check */}
                {canAddLocation && (
                  <div className="mt-6">
                    <button
                      onClick={handleAddLocation}
                      className="w-full bg-[#FDB913] text-black rounded-lg py-3 px-4 font-medium hover:bg-[#e5a711] transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add New Location
                    </button>
                    <p className="text-gray-500 text-sm text-center mt-2">
                      {locations.length} of {locationLimit} locations used
                    </p>
                  </div>
                )}

                {/* Show limit reached message */}
                {!canAddLocation && isMultiLocationPlan && (
                  <div className="mt-6 text-center">
                    <p className="text-gray-500 text-sm">
                      Location limit reached ({locations.length} of {locationLimit})
                    </p>
                    {restaurantData?.subscription_plan === 'delicious' && (
                      <p className="text-[#FDB913] text-sm mt-1">
                        Upgrade to Gourmet plan for up to 4 locations
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Team Management Section */}
            {activeSection === 'team' && (
              <TeamManagement />
            )}

            {/* Subscription Section */}
            {activeSection === 'subscription' && (
              <SubscriptionManagement />
            )}

            {/* Purchase History Section */}
            {activeSection === 'purchases' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Billing History</h2>
                <p className="text-gray-600 mb-6">View your complete billing and invoice history</p>

                <div className="flex flex-col gap-4">
                  <button
                    onClick={async () => {
                      // Check if demo user
                      const isDemoUser = localStorage.getItem('isDemo') === 'true';

                      if (isDemoUser) {
                        toast({
                          title: "Demo Mode",
                          description: "Billing history is not available in demo mode.",
                        });
                        return;
                      }

                      // Check for valid auth token
                      const authToken = Cookies.get('auth_token');
                      if (!authToken) {
                        toast({
                          title: "Authentication Required",
                          description: "Billing portal requires a real Stripe customer.",
                        });
                        return;
                      }

                      try {
                        const { portal_url } = await stripeApi.createPortalSession();
                        window.open(portal_url, '_blank');
                      } catch (error) {
                        toast({
                          title: "Error",
                          description: "Failed to open billing portal",
                          variant: "destructive"
                        });
                      }
                    }}
                    className="w-full sm:w-auto px-6 py-3 bg-gray-100 hover:bg-[#FDB913] text-gray-700 hover:text-black rounded-lg font-medium transition-all flex items-center justify-center gap-2 border border-gray-200 hover:border-[#FDB913]"
                  >
                    <ExternalLink className="w-5 h-5" />
                    View Billing History
                  </button>

                  <p className="text-gray-500 text-sm">
                    Access your complete billing history, download invoices, and update payment methods through our secure Stripe portal.
                  </p>
                </div>
              </div>
            )}

            {/* Notifications Section */}
            {activeSection === 'notifications' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Email Preferences</h2>
                <p className="text-gray-600 mb-6">Control which emails you receive from Dine AI</p>

                <div className="space-y-4">
                  <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium">Weekly Performance Reports</p>
                      <p className="text-gray-500 text-sm mt-1">Get insights about your restaurant's performance every week</p>
                    </div>
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded text-[#FDB913] focus:ring-[#FDB913] focus:ring-offset-0 bg-white border-gray-300"
                      checked={notificationPrefs.weekly_reports}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        console.log('Changing weekly_reports to:', newValue);
                        setNotificationPrefs(prev => ({ ...prev, weekly_reports: newValue }));
                        setSavingNotifications(true);
                        try {
                          const response = await apiClient.put('/settings/notifications', {
                            notification_weekly_reports: newValue,
                            notification_marketing_tips: notificationPrefs.marketing_tips
                          });
                          console.log('Save response:', response.data);
                        } catch (error: any) {
                          console.error('Failed to save notification preferences:', error);
                          console.error('Error details:', error.response?.data);
                          alert('Failed to save preference. Please try again.');
                          // Revert on error
                          setNotificationPrefs(prev => ({ ...prev, weekly_reports: !newValue }));
                        } finally {
                          setSavingNotifications(false);
                        }
                      }}
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium">Marketing Tips & Best Practices</p>
                      <p className="text-gray-500 text-sm mt-1">Receive tips to help grow your restaurant business</p>
                    </div>
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded text-[#FDB913] focus:ring-[#FDB913] focus:ring-offset-0 bg-white border-gray-300"
                      checked={notificationPrefs.marketing_tips}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        console.log('Changing marketing_tips to:', newValue);
                        setNotificationPrefs(prev => ({ ...prev, marketing_tips: newValue }));
                        setSavingNotifications(true);
                        try {
                          const response = await apiClient.put('/settings/notifications', {
                            notification_weekly_reports: notificationPrefs.weekly_reports,
                            notification_marketing_tips: newValue
                          });
                          console.log('Save response:', response.data);
                        } catch (error: any) {
                          console.error('Failed to save notification preferences:', error);
                          console.error('Error details:', error.response?.data);
                          alert('Failed to save preference. Please try again.');
                          // Revert on error
                          setNotificationPrefs(prev => ({ ...prev, marketing_tips: !newValue }));
                        } finally {
                          setSavingNotifications(false);
                        }
                      }}
                    />
                  </label>
                </div>

                {savingNotifications && (
                  <div className="mt-4 text-center text-gray-500 text-sm">Saving...</div>
                )}
              </div>
            )}

          </motion.div>
        </div>
      </div>

      {/* Location Edit Modal */}
      <LocationEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingLocation(null);
        }}
        location={editingLocation}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default RestaurantSettings;